import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Button,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  Switch
} from '@material-ui/core';
import {
  Delete,
  Refresh,
  CheckCircleOutline,
  HighlightOff,
  ArrowBack,
  Add as AddIcon
} from '@material-ui/icons';
import {
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Autocomplete, Stack } from '@mui/material';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import MainContainer from '../../components/MainContainer';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
  },
  webhookUrl: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  webhookUrlText: {
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  copyButton: {
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  fieldContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  lastRequestContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
    border: `1px solid ${theme.palette.divider}`,
  },
  keyValuePair: {
    display: 'flex',
    padding: theme.spacing(0.5),
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  keyChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    marginRight: theme.spacing(1),
  },
  valueText: {
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  select: {
    minWidth: 200,
  },
  chip: {
    margin: 2,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  addFieldButton: {
    marginTop: theme.spacing(2),
  },
}));

const WebhookConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const { hashId } = useParams();
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [flowsData, setFlowsData] = useState([]);
  const [flowsDataObj, setFlowsDataObj] = useState([]);
  const [btnAtt, setBtnAtt] = useState(false);
  const [extraInputs, setExtrasInputs] = useState([]);
  const [lastRequest, setLastRequest] = useState(null);

  const [keysJson, setKeysJson] = useState([]);
  const [keysJsonDraw, setKeysJsonDraw] = useState([]);
  const [keysJsonFull, setKeysJsonFull] = useState([]);
  const [keysJsonValue, setKeysJsonValue] = useState([]);

  const [detailsValues, setDetailsValues] = useState({});
  const [activeWebhookState, setActiveWebhookState] = useState(false);
  const [numberPhone, setNumberPhone] = useState([]);
  const [nameClient, setNameClient] = useState([]);
  const [emailClient, setEmailClient] = useState([]);
  const [flowSelected, setFlowSelected] = useState(null);
  const [selectedChatsapps, setSelectedWhatsapps] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  // Campos adicionais
  const [additionalFields, setAdditionalFields] = useState([]);

  // Estado para desabilitar/ativar botões
  const [disabledButton, setDisabledButton] = useState(true);

  useEffect(() => {
    getPage();
  }, [hashId]);

  const getPage = async () => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchConnections = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            // Filtra para manter apenas as conexões de WhatsApp
            const onlyWhatsConnections = data.filter(
              (conn) => conn.channel === "whatsapp"  // Ajuste a condição conforme a sua API
            );
      
            // Depois pega apenas o name de cada conexão
            setWhatsapps(onlyWhatsConnections.map((item) => item.name));
          })
          .catch((err) => {
            toastError(err);
          });
      };      

      fetchConnections();

      const fetchWebhookConfig = async () => {
        try {
          const { data } = await api.get(`/webhook/config/${hashId}`);
          setWebhooks(data.webhook);
          setActiveWebhookState(data.webhook.active);

          const flows = await api.get('/flowbuilder');
          setFlowsData(flows.data.flows.map((flow) => flow.name));
          setFlowsDataObj(flows.data.flows);

          if (data.webhook.config !== null) {
            setDisabledButton(data.webhook.config['details'] ? false : true);

            setLastRequest(data.webhook.config.lastRequest);

            const values = await getAllValues(data.webhook.config.lastRequest);
            setKeysJsonValue(values);

            const keys = await getAllKeys(data.webhook.config.lastRequest);
            setKeysJson(keys);

            const keysDraw = await getAllKeysWithValues(
              data.webhook.config.lastRequest
            );
            setKeysJsonDraw(keysDraw);

            const fullKeys = await getAllKeysFull(
              data.webhook.config.lastRequest
            );
            setKeysJsonFull(fullKeys);

            // Se existir config.details, carregamos os inputs
            if (data.webhook.config.details) {
              const connections = data.webhook.config.details.connections;
              if (Array.isArray(connections)) {
                setSelectedWhatsapps(
                  connections.map((item) => item.toString())
                );
              }

              setDetailsValues(data.webhook.config.details);

              // Carrega os inputs
              getValueForKeyValue(data.webhook.config.details.inputs);
              getFlowSelect(data.webhook.config.details.idFlow, flows.data.flows);
              removeItemWithKeyValue(data.webhook.config.details);
            }
          }
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchWebhookConfig();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  };

  function getAllKeys(obj) {
    const keys = [];
    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (Array.isArray(obj[key])) {
          const nestedKeys = obj[key].map((_, index) => `${key}[${index}]`);
          keys.push(...nestedKeys);
        } else {
          const nestedKeys = getAllKeys(obj[key]);
          keys.push(...nestedKeys);
        }
      } else {
        keys.push(key);
      }
    }
    return keys;
  }

  function getAllKeysWithValues(obj, parentKey = '') {
    const keysWithValues = [];
    for (const key in obj) {
      const currentKey = parentKey ? `${parentKey}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (Array.isArray(obj[key])) {
          obj[key].forEach((value, index) => {
            keysWithValues.push(`${currentKey}[${index}]`);
          });
        } else {
          const nestedKeysWithValues = getAllKeysWithValues(obj[key], currentKey);
          keysWithValues.push(...nestedKeysWithValues);
        }
      } else {
        keysWithValues.push(currentKey);
      }
    }
    return keysWithValues;
  }

  async function getAllKeysFull(dados, prefixo = '', caminhos = []) {
    for (const chave in dados) {
      const caminhoCompleto = prefixo ? `${prefixo}.${chave}` : chave;
      if (typeof dados[chave] === 'object' && dados[chave] !== null) {
        getAllKeysFull(dados[chave], caminhoCompleto, caminhos);
      } else {
        caminhos.push(caminhoCompleto);
      }
    }
    return caminhos;
  }

  function getAllValues(obj) {
    const values = [];
    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (Array.isArray(obj[key])) {
          values.push(...obj[key]);
        } else {
          values.push(...getAllValues(obj[key]));
        }
      } else {
        values.push(obj[key]);
      }
    }
    return values;
  }

  function getFlowSelect(flowId, flowObj) {
    const name = flowObj.filter((item) => item.id === flowId);
    if (name.length > 0) {
      setFlowSelected(name[0].name);
    }
  }

  function removeItemWithKeyValue(dataInput) {
    let data = dataInput.inputs;
    data = data.filter((item) => item.keyValue !== 'celular');
    data = data.filter((item) => item.keyValue !== 'nome');
    data = data.filter((item) => item.keyValue !== 'email');

    data = data.map((item) => {
      return { ...item, data: item.data.split(',') };
    });

    // armazena em extraInputs (se precisar)
    setExtrasInputs(data);
  }

  function getValueForKeyValue(data) {
    const additionalField = [];
    data.forEach((item) => {
      if (item.keyValue === 'celular') {
        if (item.data === '') {
          return setNumberPhone([]);
        }
        setNumberPhone(item.data.split(','));
      } else if (item.keyValue === 'nome') {
        if (item.data === '') {
          return setNameClient([]);
        }
        setNameClient(item.data.split(','));
      } else if (item.keyValue === 'email') {
        if (item.data === '') {
          return setEmailClient([]);
        }
        setEmailClient(item.data.split(','));
      } else {
        // qualquer outro campo adicional
        additionalField.push({
          name: item.keyValue,
          value: Array.isArray(item.data) ? item.data.join(',') : item.data,
          saveToContact: item.saveToContact ?? false
        });
      }
    });
    setAdditionalFields(additionalField);
  }

  // handlers
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedWhatsapps(
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const handleAddField = () => {
    setAdditionalFields([...additionalFields, { name: '', value: '', saveToContact: false }]);
  };

  const handleRemoveField = (index) => {
    const newFields = additionalFields.filter((_, i) => i !== index);
    setAdditionalFields(newFields);
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...additionalFields];
    newFields[index][field] = value;
    setAdditionalFields(newFields);
  };

  const saveWebhook = async () => {
    if (numberPhone.length === 0) {
      return toast.error('Número de celular é obrigatório');
    }
    if (nameClient.length === 0) {
      return toast.error('Nome é obrigatório');
    }
    if (flowSelected === null) {
      return toast.error('Fluxo é obrigatório');
    }
    if (whatsapps.length === 0) {
      return toast.error('Conexão é obrigatória');
    }
    if (!selectedChatsapps.length) {
      return toast.error('Conexão não pode ficar vazia.');
    }

    setDisabledButton(false);

    let arrayData = [];

    // Adiciona campos base (nome, email, celular)
    arrayData.push({
      order: 0,
      keyValue: 'nome',
      data: nameClient.toString(),
    });
    arrayData.push({
      order: 1,
      keyValue: 'email',
      data: emailClient.toString(),
    });
    arrayData.push({
      order: 2,
      keyValue: 'celular',
      data: numberPhone.toString(),
    });

    // Adiciona campos adicionais + saveToContact
    let currentOrder = 3;
    additionalFields.forEach((field) => {
      if (field.name && field.value) {
        arrayData.push({
          order: currentOrder,
          keyValue: field.name,
          data: field.value,
          saveToContact: field.saveToContact
        });
        currentOrder++;
      }
    });

    // Pega ID do fluxo
    const chosenFlow = flowsDataObj.find((flow) => flow.name === flowSelected);
    const idFflow = chosenFlow ? chosenFlow.id : null;

    try {
      await api.put('/webhook/config', {
        config: {
          inputs: arrayData,
          idFlow: idFflow,
          keysFull: keysJsonFull,
          connections: selectedChatsapps
        },
        webhookId: webhooks.id,
      });
      toast.success(i18n.t('webhookModal.toasts.update'));
    } catch (err) {
      toastError(err);
    }
  };

  const activeWebhook = async (status) => {
    try {
      await api.put('/webhook/config/active', {
        status: status,
        webhookId: webhooks.id,
      });
      setActiveWebhookState(status);
      toast.success('Webhook ' + (status ? 'ativado!' : 'desativado'));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <div className={classes.titleContainer}>
          <Button
            variant="outlined"
            color="primary"
            className={classes.backButton}
            startIcon={<ArrowBack />}
            onClick={() => history.push('/webhooks')}
          >
            Voltar
          </Button>
          <Title>{i18n.t('webhook.configtitle')}</Title>
        </div>
        <MainHeaderButtonsWrapper>
          {!loading && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => activeWebhook(!activeWebhookState)}
              startIcon={activeWebhookState ? <HighlightOff /> : <CheckCircleOutline />}
              disabled={disabledButton}
            >
              {activeWebhookState ? 'Desativar' : 'Ativar'}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {!loading ? (
        <Paper className={classes.mainPaper}>
          <div className={classes.webhookUrl}>
            <Typography className={classes.webhookUrlText}>
              {`${process.env.REACT_APP_BACKEND_URL}/webhook/${webhooks.company_id}/${webhooks.hash_id}`}
            </Typography>
            <IconButton
              className={classes.copyButton}
              onClick={() =>
                navigator.clipboard.writeText(
                  `${process.env.REACT_APP_BACKEND_URL}/webhook/${webhooks.company_id}/${webhooks.hash_id}`
                )
              }
            >
              <CopyIcon />
            </IconButton>
          </div>

          <div className={classes.fieldContainer}>
            <Typography className={classes.sectionTitle}>
              Última requisição recebida
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" marginBottom={2}>
              <Button
                variant="outlined"
                color="primary"
                disabled={btnAtt}
                onClick={() => getPage()}
                startIcon={<Refresh />}
              >
                Atualizar dados
              </Button>
            </Stack>
            <div className={classes.lastRequestContainer}>
              {keysJson.map((item, index) => (
                <div key={index} className={classes.keyValuePair}>
                  <Chip
                    label={item}
                    size="small"
                    className={classes.keyChip}
                  />
                  <Typography className={classes.valueText}>
                    {typeof keysJsonValue[index] === 'object'
                      ? 'null'
                      : keysJsonValue[index]}
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          <div className={classes.fieldContainer}>
            <Typography className={classes.sectionTitle}>
              Configuração do Webhook
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography gutterBottom>Número do celular do cliente</Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={keysJsonDraw}
                  value={numberPhone}
                  onChange={(event, newValue) => setNumberPhone(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione os campos"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        className={classes.chip}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>Nome do cliente</Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={keysJsonDraw}
                  value={nameClient}
                  onChange={(event, newValue) => setNameClient(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione os campos"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        className={classes.chip}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>Email do cliente</Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={keysJsonDraw}
                  value={emailClient}
                  onChange={(event, newValue) => setEmailClient(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione os campos"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        className={classes.chip}
                      />
                    ))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>Conexões</Typography>
                <Select
                  multiple
                  value={selectedChatsapps}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  renderValue={(selected) => (
                    <div>
                      {selected?.map((value) => (
                        <Chip
                          key={value}
                          label={whatsapps.find((w) => w === value)}
                          className={classes.chip}
                          variant="outlined"
                        />
                      ))}
                    </div>
                  )}
                >
                  {whatsapps.map((wpp) => (
                    <MenuItem key={wpp} value={wpp}>
                      {wpp}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>Fluxo</Typography>
                <Autocomplete
                  options={flowsData}
                  value={flowSelected}
                  onChange={(event, newValue) => setFlowSelected(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Escolha um fluxo"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>

          {/* CAMPOS ADICIONAIS */}
          <div className={classes.fieldContainer}>
            <Typography className={classes.sectionTitle}>
              Campos Adicionais
            </Typography>

            {additionalFields.map((field, index) => (
              <Grid container spacing={2} key={index} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Nome do Campo"
                    variant="outlined"
                    value={field.name}
                    onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    freeSolo
                    options={keysJsonDraw}
                    value={field.value}
                    onChange={(_, newValue) => handleFieldChange(index, 'value', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Valor"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={2}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="body2" style={{ marginRight: 8 }}>
                    Salvar no Contato?
                  </Typography>
                  <Switch
                    checked={field.saveToContact}
                    onChange={(event) =>
                      handleFieldChange(index, 'saveToContact', event.target.checked)
                    }
                    color="primary"
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={1}
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  <IconButton color="secondary" onClick={() => handleRemoveField(index)}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddField}
              className={classes.addFieldButton}
              fullWidth
            >
              Adicionar Campo
            </Button>
          </div>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={saveWebhook}
            style={{ marginTop: 24 }}
          >
            Salvar webhook
          </Button>
        </Paper>
      ) : (
        <Stack justifyContent="center" alignItems="center" minHeight="70vh">
          <CircularProgress />
        </Stack>
      )}
    </MainContainer>
  );
};

export default WebhookConfig;
